import type { Decision, NumberAnswer } from "../childInput/toddlerPhonetics";
import type { TeacherState } from "../livingAvatar/livingAvatar";
import { GOLDEN_SCENES, type GoldenSceneId, type GoldenSceneSpec } from "./goldenLessonData";

export type GoldenLoopPhase =
  | "INIT"
  | "TEACHER_SPEAKING"
  | "CHILD_LISTENING"
  | "TEACHER_LISTENING"
  | "CHILD_ACKNOWLEDGED"
  | "HONEST_REACTION"
  | "SCAFFOLD_HINT"
  | "SCENE_COMPLETE"
  | "SESSION_COMPLETE";

export type ChildInteractionRecord =
  | { type: "PARTICIPATED"; modality: "VOICE" | "CLAP" | "TOUCH" }
  | { type: "SEMANTIC_DECISION"; decision: Decision; modality: "VOICE" | "TOUCH" }
  | { type: "SILENCE_OBSERVED" }
  | { type: "SCAFFOLD_ASSISTED" };

export type InputModality = "VOICE" | "CLAP" | "TOUCH";

export const MIN_ACKNOWLEDGE_BREATH_GAP_MS = 800;

export interface CommunicationLatencies {
  tA: number | null;
  tB: number | null;
  tC: number | null;
  total: number | null;
  tAckEndToFeedbackStart?: number | null;
}

export interface GoldenSessionState {
  readonly sceneIndex: number;
  readonly phase: GoldenLoopPhase;
  readonly currentScene: GoldenSceneSpec;
  readonly teacherState: TeacherState;
  readonly lastRecord: ChildInteractionRecord | null;
  readonly retryCount: number;
  readonly latencies: CommunicationLatencies;
  readonly inputSource: InputModality | null;
}

export interface GoldenEngineObserver {
  readonly onStateChanged: (state: GoldenSessionState) => void;
  readonly onPlayTeacherAudio: (cueId: string, onEnd: () => void) => void;
  readonly onPlayFeedbackAudio: (feedbackType: "CORRECT" | "WRONG" | "SILENCE", onEnd: () => void) => void;
  readonly onPlayAcknowledgeAudio?: (onEnd: () => void, source?: InputModality) => void;
}

export class GoldenEngine {
  #sceneIndex = 0;
  #phase: GoldenLoopPhase = "INIT";
  #teacherState: TeacherState = "IDLE";
  #lastRecord: ChildInteractionRecord | null = null;
  #retryCount = 0;
  #isDisposed = false;
  #inputSource: InputModality | null = null;
  #latencies: CommunicationLatencies = { tA: null, tB: null, tC: null, total: null };
  #ackAudioEndedAtMs: number | null = null;
  #isAckAudioPlaying = false;
  #pendingFeedbackTimeout: number | null = null;
  #pendingFeedbackAction: (() => void) | null = null;
  #advanceTimer: ReturnType<typeof setTimeout> | null = null;
  #sessionEpoch = 0;

  constructor(readonly observer: GoldenEngineObserver) {}

  get latencies(): CommunicationLatencies {
    return { ...this.#latencies };
  }

  get sessionEpoch(): number {
    return this.#sessionEpoch;
  }

  get state(): GoldenSessionState {
    const currentScene = GOLDEN_SCENES[this.#sceneIndex] ?? GOLDEN_SCENES[0]!;
    return {
      sceneIndex: this.#sceneIndex,
      phase: this.#phase,
      currentScene,
      teacherState: this.#teacherState,
      lastRecord: this.#lastRecord,
      retryCount: this.#retryCount,
      latencies: this.#latencies,
      inputSource: this.#inputSource,
    };
  }

  start(): void {
    this.#sceneIndex = 0;
    this.#retryCount = 0;
    this.#startCurrentScene();
  }

  #notify(): void {
    if (this.#isDisposed) return;
    this.observer.onStateChanged(this.state);
  }

  #startCurrentScene(): void {
    const scene = GOLDEN_SCENES[this.#sceneIndex];
    if (!scene) {
      this.#phase = "SESSION_COMPLETE";
      this.#teacherState = "CELEBRATE";
      this.#notify();
      return;
    }

    this.#phase = "TEACHER_SPEAKING";
    this.#teacherState = "SPEAK";
    this.#lastRecord = null;
    this.#latencies = { tA: null, tB: null, tC: null, total: null };
    this.#notify();

    // 1. JINAH SPEAKS
    this.observer.onPlayTeacherAudio(scene.audioCueId, () => {
      if (this.#isDisposed || this.#phase !== "TEACHER_SPEAKING") return;

      // 2. CHILD LISTENS -> JINAH WAITS & LISTENS
      this.#phase = "TEACHER_LISTENING";
      this.#teacherState = "LISTEN";
      this.#notify();
    });
  }

  // Two-Phase Phase 1: Immediate Acknowledge ("들었어" - VAD != CORRECT, Touch != VAD)
  handleAcknowledge(
    latencyOrSource?: number | null | InputModality,
    maybeSource?: InputModality,
  ): void {
    // Q05: Accept input during both TEACHER_LISTENING and SCAFFOLD_HINT
    if (this.#phase !== "TEACHER_LISTENING" && this.#phase !== "SCAFFOLD_HINT") return;
    this.#clearFeedbackTimer();

    let latencyMs: number | null = null;
    let source: InputModality = "VOICE";

    if (typeof latencyOrSource === "number") {
      latencyMs = latencyOrSource;
      source = maybeSource ?? "VOICE";
    } else if (typeof latencyOrSource === "string") {
      source = latencyOrSource;
      latencyMs = null; // Q04: No synthetic latency fallback. Unmeasured = null.
    } else {
      latencyMs = null;
      source = maybeSource ?? "VOICE";
    }

    this.#inputSource = source;
    this.#latencies.tA = latencyMs;
    this.#phase = "CHILD_ACKNOWLEDGED";
    this.#teacherState = "ACKNOWLEDGE";
    this.#isAckAudioPlaying = true;
    this.#ackAudioEndedAtMs = null;
    this.#clearFeedbackTimer();
    this.#notify();

    const scene = GOLDEN_SCENES[this.#sceneIndex];
    const onAckEnded = () => {
      if (this.#isDisposed) return;
      this.#ackAudioEndedAtMs = performance.now();
      this.#isAckAudioPlaying = false;

      // If an explicit action was queued during ACK audio, schedule it with breath gap
      if (this.#pendingFeedbackAction) {
        const action = this.#pendingFeedbackAction;
        this.#pendingFeedbackAction = null;
        this.#pendingFeedbackTimeout = window.setTimeout(action, MIN_ACKNOWLEDGE_BREATH_GAP_MS);
        return;
      }

      // For participation activities (G1, G2, G3, G6), transition smoothly to praise after breath gap
      if (
        scene &&
        (scene.interactionType === "VOICE_IMITATION" ||
          scene.interactionType === "GREETING_PARTICIPATION" ||
          scene.interactionType === "CLAP_ACTION" ||
          scene.interactionType === "STORY_CELEBRATION")
      ) {
        if (this.#phase === "CHILD_ACKNOWLEDGED") {
          this.#pendingFeedbackTimeout = window.setTimeout(() => {
            if (this.#isDisposed) return;
            this.#pendingFeedbackTimeout = null;
            if (this.#phase === "CHILD_ACKNOWLEDGED") {
              this.handleParticipation(source);
            }
          }, MIN_ACKNOWLEDGE_BREATH_GAP_MS);
        }
      }
    };

    if (this.observer.onPlayAcknowledgeAudio) {
      this.observer.onPlayAcknowledgeAudio(onAckEnded, source);
    } else {
      // Fallback for tests without audio
      setTimeout(onAckEnded, 100);
    }
  }

  #clearFeedbackTimer(): void {
    if (this.#pendingFeedbackTimeout !== null) {
      window.clearTimeout(this.#pendingFeedbackTimeout);
      this.#pendingFeedbackTimeout = null;
    }
    this.#pendingFeedbackAction = null;
  }

  #scheduleFeedbackAudio(feedbackType: "CORRECT" | "WRONG" | "SILENCE", onEnd: () => void): void {
    this.#clearFeedbackTimer();

    const trigger = () => {
      if (this.#isDisposed) return;
      const feedbackStartTime = performance.now();
      if (this.#ackAudioEndedAtMs !== null) {
        this.#latencies.tAckEndToFeedbackStart = Math.round(feedbackStartTime - this.#ackAudioEndedAtMs);
      }
      this.observer.onPlayFeedbackAudio(feedbackType, onEnd);
    };

    if (this.#isAckAudioPlaying) {
      this.#pendingFeedbackAction = trigger;
      return;
    }

    if (this.#ackAudioEndedAtMs !== null) {
      const elapsedSinceAckEnd = performance.now() - this.#ackAudioEndedAtMs;
      const remainingMs = Math.max(0, MIN_ACKNOWLEDGE_BREATH_GAP_MS - elapsedSinceAckEnd);
      if (remainingMs > 0) {
        this.#pendingFeedbackTimeout = window.setTimeout(trigger, remainingMs);
        return;
      }
    }

    trigger();
  }

  // Handle Participation Activities (G1, G2, G3)
  handleParticipation(modality: "VOICE" | "CLAP" | "TOUCH"): void {
    if (this.#phase !== "TEACHER_LISTENING" && this.#phase !== "CHILD_ACKNOWLEDGED" && this.#phase !== "SCAFFOLD_HINT") return;
    this.#clearFeedbackTimer();

    this.#inputSource = modality;
    this.#lastRecord = { type: "PARTICIPATED", modality };
    this.#phase = "HONEST_REACTION";
    this.#teacherState = "PRAISE";
    this.#notify();

    this.#scheduleFeedbackAudio("CORRECT", () => {
      this.#advanceNextScene();
    });
  }

  // Two-Phase Phase 2: Semantic Answer Judgment (G4)
  handleSemanticDecision(decision: Decision, modality: "VOICE" | "TOUCH", latencies?: CommunicationLatencies): void {
    if (this.#phase !== "TEACHER_LISTENING" && this.#phase !== "CHILD_ACKNOWLEDGED" && this.#phase !== "SCAFFOLD_HINT") return;
    this.#clearFeedbackTimer();

    this.#inputSource = modality;
    if (latencies) {
      this.#latencies = latencies;
    } else if (modality === "TOUCH") {
      this.#latencies.tA = null;
    }

    this.#lastRecord = { type: "SEMANTIC_DECISION", decision, modality };
    this.#phase = "HONEST_REACTION";

    if (decision.verdict === "CORRECT") {
      this.#teacherState = "PRAISE";
      this.#notify();
      this.#scheduleFeedbackAudio("CORRECT", () => {
        this.#advanceNextScene();
      });
    } else if (decision.verdict === "WRONG") {
      this.#teacherState = "RETRY";
      this.#retryCount += 1;
      this.#notify();
      this.#scheduleFeedbackAudio("WRONG", () => {
        // Return to listening for child retry
        this.#phase = "TEACHER_LISTENING";
        this.#teacherState = "LISTEN";
        this.#notify();
      });
    } else {
      // UNCERTAIN: Do not fake answer! Offer gentle prompt & stay listening
      this.#teacherState = "WAIT";
      this.#notify();
      this.#scheduleFeedbackAudio("SILENCE", () => {
        this.#phase = "TEACHER_LISTENING";
        this.#teacherState = "LISTEN";
        this.#notify();
      });
    }
  }

  handleSilenceTimeout(): void {
    if (this.#phase !== "TEACHER_LISTENING" && this.#phase !== "CHILD_ACKNOWLEDGED") return;

    this.#lastRecord = { type: "SILENCE_OBSERVED" };
    this.#phase = "SCAFFOLD_HINT";
    this.#teacherState = "MODEL";
    this.#notify();

    this.#scheduleFeedbackAudio("SILENCE", () => {
      // After scaffold hint, child can retry or tap
      this.#phase = "TEACHER_LISTENING";
      this.#teacherState = "LISTEN";
      this.#lastRecord = { type: "SCAFFOLD_ASSISTED" };
      this.#notify();
    });
  }

  #clearAdvanceTimer(): void {
    if (this.#advanceTimer !== null) {
      clearTimeout(this.#advanceTimer);
      this.#advanceTimer = null;
    }
  }

  #advanceNextScene(): void {
    if (this.#isDisposed) return;
    this.#clearFeedbackTimer();
    this.#clearAdvanceTimer();
    this.#phase = "SCENE_COMPLETE";
    this.#notify();

    const currentEpoch = this.#sessionEpoch;
    // Q06: Natural toddler pacing pause (1.2s) before next scene begins, bound to session epoch
    this.#advanceTimer = setTimeout(() => {
      this.#advanceTimer = null;
      if (this.#isDisposed || this.#sessionEpoch !== currentEpoch) return;
      this.#sceneIndex += 1;
      this.#retryCount = 0;
      this.#startCurrentScene();
    }, 1200);
  }

  restart(): void {
    this.#clearFeedbackTimer();
    this.#clearAdvanceTimer();
    this.#sessionEpoch += 1;
    this.#sceneIndex = 0;
    this.#retryCount = 0;
    this.#startCurrentScene();
  }

  jumpToScene(index: number): void {
    if (this.#isDisposed || index < 0 || index >= GOLDEN_SCENES.length) return;
    this.#clearFeedbackTimer();
    this.#clearAdvanceTimer();
    this.#sessionEpoch += 1;
    this.#sceneIndex = index;
    this.#retryCount = 0;
    this.#startCurrentScene();
  }

  dispose(): void {
    this.#clearFeedbackTimer();
    this.#clearAdvanceTimer();
    this.#sessionEpoch += 1;
    this.#isDisposed = true;
  }
}
